import type { FC } from 'react';

import { t } from '@lingui/core/macro';
import { CalendarMinus02 } from '@untitledui/icons/CalendarMinus02';
import { EyeOff } from '@untitledui/icons/EyeOff';
import { Rows03 } from '@untitledui/icons/Rows03';
import { SlashCircle02 } from '@untitledui/icons/SlashCircle02';
import { Star01 } from '@untitledui/icons/Star01';
import { UserX01 } from '@untitledui/icons/UserX01';

import { MultiSelectDropdown } from '@/components/base/select/multi-select-dropdown';

/** The non-default views — the values allowed in the `view` search param. */
export const PARTICIPANT_VISIBILITY_URL_VALUES = [
  'favorite',
  'hidden',
  'rejected',
  'no-show',
  'cancelled',
] as const;

export type ParticipantVisibilityUrlValue =
  (typeof PARTICIPANT_VISIBILITY_URL_VALUES)[number];

// Single-select visibility views over the participants list. "all" is the
// default (non-hidden roster) and is kept out of the URL. How each view
// interacts with the other filters and the API lives in `participant-search`.
export const PARTICIPANT_VISIBILITY_FILTER_VALUES = [
  ...PARTICIPANT_VISIBILITY_URL_VALUES,
  'all',
] as const;

export type ParticipantVisibilityFilter =
  (typeof PARTICIPANT_VISIBILITY_FILTER_VALUES)[number];

// The `Record` return type enforces an entry for every filter value at
// compile time.
function getVisibilityLabels(): Record<
  ParticipantVisibilityFilter,
  { label: string; icon: FC<{ className?: string }> | undefined }
> {
  return {
    all: { label: t`Default view`, icon: Rows03 },
    favorite: { label: t`Favorited`, icon: Star01 },
    hidden: { label: t`Hidden`, icon: EyeOff },
    rejected: { label: t`Rejected`, icon: UserX01 },
    'no-show': { label: t`No show`, icon: CalendarMinus02 },
    cancelled: { label: t`Cancelled`, icon: SlashCircle02 },
  };
}

interface ParticipantVisibilityFilterProps {
  value: ParticipantVisibilityFilter;
  onChange: (next: ParticipantVisibilityFilter) => void;
}

export function ParticipantVisibilityFilter({
  value,
  onChange,
}: ParticipantVisibilityFilterProps) {
  const labels = getVisibilityLabels();
  const items = PARTICIPANT_VISIBILITY_FILTER_VALUES.map((id) => ({
    id,
    label: labels[id].label,
    icon: labels[id].icon,
  }));

  return (
    <MultiSelectDropdown<ParticipantVisibilityFilter>
      items={items}
      size="xs"
      selectedKeys={new Set([value])}
      onSelectionChange={(next) => onChange([...next][0] ?? 'all')}
      selectionMode="single"
      triggerVariant="button"
      placeholder={t`Visibility`}
      triggerPrefix={t`Visibility`}
      triggerHiddenKeys={['all']}
      noWrap
      matchTriggerWidth={false}
      popoverPlacement="bottom start"
      popoverClassName="min-w-[200px]"
      aria-label={t`Filter by visibility`}
    />
  );
}
